/** @jsxImportSource @emotion/react */
import { faFileArrowDown, faFileArrowUp } from "@fortawesome/pro-regular-svg-icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Select from "react-select";
import { Input } from "reactstrap";
import useConfig from "../../../actions/Tenants/config/configHook";
import { toast } from "../../../utils/toast";
import { callAvosApi } from "../../../utils/useAvosApiHook";
import { Button } from "../../Helper/Button";
import { getTableHead, Table, TableCell, TableHeaderCell } from "../../Helper/Table";
import { formatDateTime } from "../Layers/fields/formatters";
import FullPageLayout from "../Layout/FullPageLayout";
import { ListStatusBadge } from "../PackingList/PackingListList";
import TruncatedFileName from "../PackingList/TruncatedFileName";
import { useFields, usePagination } from "../../../hooks";

const SourcingList = () => {
    const [file, setFile] = useState<File | null>(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const config = useConfig();
    const queryClient = useQueryClient();
    const { offset, limit } = usePagination();
    const listType = searchParams.get("type") || "pallet";
    const inputRef = useRef<any>(null);
    const [inputKey, setInputKey] = useState("input-key");

    const getPackingLists = async (offset, limit, listType) => {
        const res = await callAvosApi(`/packing-list/?limit=${limit}&offset=${offset}&template=sourcing_${listType}`);
        return res.data;
    };

    const { data, isLoading } = useQuery({
        queryKey: ["packing-lists", offset, limit, listType],
        queryFn: () => getPackingLists(offset, limit, listType),
    });

    const invalidateQueries = () => {
        queryClient.invalidateQueries({ queryKey: ["packing-lists"], exact: false });
    };


    const { mutate: importItems, isPending } = useMutation({
        mutationFn: async (file: File) => {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("fruit_type", "avocado");

            const res = await callAvosApi(`/packing-list/import/${listType}`, {
                method: "POST",
                data: formData,
                headers: {
                    "Content-Type": "multipart/form-data",
                },

            });

            if (res.data) {
                const createdCount = res.data.created_count || 0;
                const updatedCount = res.data.updated_count || 0;

                if (createdCount || updatedCount) {
                    toast.success(
                        `${(createdCount ? `${createdCount} ${listType}s created` : "")
                        + (createdCount && updatedCount ? " and " : "")
                        + (updatedCount ? `${updatedCount} ${listType}s updated` : "")
                        } successfully.`
                    );
                    setFile(null);
                    setInputKey(Math.random().toString(36));
                }
            }
        },
        onSuccess: () => {
            invalidateQueries();
        },
        onError: (error: any) => {
            toast.error(error?.message || `An error occurred while importing ${listType}s.`);
        }
    });

    const handleImport = () => {
        if (!file) return;

        importItems(file);
    };

    const download = (item) => {
        window.location.href = item.url;
    };

    const { applyDisplayFilterLayerFields } = useFields();
    const fields = applyDisplayFilterLayerFields(config.root_config.sourcing_list_table_fields || [], {});

    // TODO BRUNA: MAKE PACKING LIST TABLE REUSABLE AND USE IT HERE
    const header = useMemo(() => (
        <tr className="position-relative">
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>File name</TableHeaderCell>

            {getTableHead(fields)}

            <TableHeaderCell>Created</TableHeaderCell>
            <TableHeaderCell sticky />
        </tr>
    ), [fields.length]);

    return (
        <FullPageLayout title={`${listType.charAt(0).toUpperCase() + listType.slice(1)} Import`}>
            <div className="w-100 d-flex align-items-center justify-content-end gap-2 mb-4" style={{ zIndex: 100, position: "relative" }}>
                <Input
                    ref={inputRef as any}
                    id="exampleFile"
                    key={inputKey}
                    name="file"
                    type="file"
                    accept=".csv,.xlsx,.xlsm,.xls, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, application/vnd.oasis.opendocument.spreadsheet, application/vnd.ms-excel.sheet.macroEnabled.12"
                    onChange={(e) => setFile(e.target.files?.[0] || null)}
                    className="w-auto"
                />

                <Button color="primary" onClick={handleImport} icon={faFileArrowUp} disabled={!file} isLoading={isPending}>
                    Import {listType}s
                </Button>

                <Select
                    options={[
                        { value: "pallet", label: "Pallet" },
                        { value: "order", label: "Order" },
                    ]}
                    value={{ value: listType, label: listType.charAt(0).toUpperCase() + listType.slice(1) }}
                    onChange={(option) => setSearchParams({ type: option?.value ?? "pallet" })}
                />
            </div>
            {/* TODO BRUNA: MAKE PACKING LIST TABLE REUSABLE */}
            <Table
                header={header}
                count={data?.count || 0}
                isLoading={isLoading}
                withWrapper
                emptyStateText="Upload a pallet list to get started"
            >
                <tbody>
                    {(data?.results ?? []).map((item, index) => (
                        <tr key={index} title={item.id} className="position-relative">
                            <TableCell><ListStatusBadge item={item}></ListStatusBadge></TableCell>
                            <TableCell width="100%"><TruncatedFileName name={item.file_name}></TruncatedFileName></TableCell>

                            {fields.map((field, fieldIndex) => (
                                <TableCell key={fieldIndex}>
                                    {field.set_value({
                                        config,
                                        layer: item.layer ?? item,
                                        check: null, // * To be used in check tables and layer overview
                                        fruit: null, // * Fruit list is not available on overview screen
                                        children: null, // * children is only available on layer index page
                                        form_options: [] // * used to translate form values to labels
                                    }).display_component()}
                                </TableCell>
                            ))}

                            <TableCell>{formatDateTime(item.created)}</TableCell>

                            <TableCell sticky>
                                <Button iconOnlyBreakpoint="md" size="sm" className="text-nowrap" color="light" icon={faFileArrowDown} onClick={() => download(item)}>Download</Button>
                            </TableCell>
                        </tr>
                    ))}
                </tbody>
            </Table>
        </FullPageLayout>
    );
};

export default SourcingList;

